import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Wifi, 
  Smartphone,
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Building2,
  DollarSign,
  Package,
  Clock,
  AlertTriangle,
  CreditCard,
  RefreshCcw
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const COLORS = ['#1E293B', '#F97316', '#059669', '#DC2626', '#8B5CF6', '#EC4899'];

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [corrigindo, setCorrigindo] = useState(false);

  useEffect(function() {
    fetchStats();
  }, []);

  const fetchStats = async function() {
    try {
      const response = await axios.get(API + '/dashboard/stats', {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      handleApiError(error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const sincronizarIXC = async function() {
    setSyncing(true);
    try {
      const response = await axios.post(API + '/ixc/sync-completo', {}, {
        headers: getAuthHeader(),
        timeout: 180000  // 3 minutos de timeout
      });
      const data = response.data;
      
      toast.success(
        `Sincronização concluída! ${data.clientes_criados || 0} criado(s), ${data.clientes_atualizados || 0} atualizado(s). Total: ${data.total_final_geral || 0}`,
        { duration: 5000 }
      );
      
      // Recarregar estatísticas após sincronização
      fetchStats();
    } catch (error) {
      handleApiError(error);
      toast.error('Erro ao sincronizar com IXC');
    } finally {
      setSyncing(false);
    }
  };

  const corrigirClassificacoes = async function() {
    setCorrigindo(true);
    try {
      const response = await axios.post(API + '/planos/corrigir-tipos', {}, {
        headers: getAuthHeader()
      });
      const data = response.data;
      
      const totalCorrigidos = data.clientes_atualizados_para_movel + data.clientes_atualizados_para_fibra;
      
      if (totalCorrigidos > 0) {
        toast.success(
          `Classificações corrigidas! ${data.clientes_atualizados_para_fibra} para Fibra, ${data.clientes_atualizados_para_movel} para Móvel.`,
          { duration: 5000 }
        );
      } else {
        toast.info('Todas as classificações já estão corretas!', { duration: 3000 });
      }
      
      // Recarregar estatísticas após correção
      fetchStats();
    } catch (error) {
      handleApiError(error);
      toast.error('Erro ao corrigir classificações');
    } finally {
      setCorrigindo(false);
    }
  };

  // Funções de navegação para cada card
  const goToClientes = function(filtro) {
    navigate('/clientes?filtro=' + filtro);
  };

  const goToFinanceiro = function() {
    navigate('/financeiro');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  const tipoPlanoData = [
    { name: 'Fibra', value: stats?.clientes_fibra || 0 },
    { name: 'Móvel', value: stats?.clientes_movel || 0 }
  ].filter(item => item.value > 0);

  const tipoClienteData = [
    { name: 'Combo', value: stats?.clientes_combo || 0 },
    { name: 'Pré-pago', value: stats?.clientes_pre_pago || 0 },
    { name: 'Pós-pago', value: stats?.clientes_pos_pago || 0 },
    { name: 'Sócios', value: stats?.clientes_socios || 0 }
  ].filter(item => item.value > 0);

  const CustomTooltip = function(props) {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="text-slate-600 text-sm">{label || payload[0].name}</p>
          <p className="text-sky-500 font-semibold">{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const FinanceiroTooltip = function(props) {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="text-slate-600 text-sm font-semibold mb-2">{label}</p>
          {payload.map(function(item, index) {
            return (
              <p key={index} style={{ color: item.color }} className="text-sm">
                {item.name}: {formatCurrency(item.value)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 font-['Barlow_Condensed'] tracking-wide">
            DASHBOARD
          </h1>
          <p className="text-slate-500">Visão geral do sistema de clientes</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={corrigirClassificacoes}
            disabled={corrigindo}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="corrigir-classificacoes-button"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${corrigindo ? 'animate-spin' : ''}`} />
            {corrigindo ? 'Corrigindo...' : 'Corrigir Classificações'}
          </Button>
          <Button
            onClick={sincronizarIXC}
            disabled={syncing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sync-ixc-button"
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar Telefonia (IXC)'}
          </Button>
        </div>
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-slate-400 transition-all" 
          data-testid="total-clientes-card"
          onClick={() => goToClientes('todos')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Total de Clientes</p>
                <p className="text-3xl font-bold text-slate-800 font-['Barlow_Condensed']">
                  {stats?.total_clientes || 0}
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-blue-400 transition-all" 
          data-testid="clientes-fibra-card"
          onClick={() => goToClientes('fibra')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Clientes Fibra</p>
                <p className="text-3xl font-bold text-blue-600 font-['Barlow_Condensed']">
                  {stats?.clientes_fibra || 0}
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center">
                <Wifi className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-purple-400 transition-all" 
          data-testid="clientes-movel-card"
          onClick={() => goToClientes('movel')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Clientes Móvel</p>
                <p className="text-3xl font-bold text-purple-600 font-['Barlow_Condensed']">
                  {stats?.clientes_movel || 0}
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-purple-500 flex items-center justify-center">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-emerald-400 transition-all" 
          data-testid="clientes-ativos-card"
          onClick={() => goToClientes('ativos')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Clientes Ativos</p>
                <p className="text-3xl font-bold text-emerald-600 font-['Barlow_Condensed']">
                  {stats?.clientes_ativos || 0}
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-emerald-600 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards - Row 2: Vencimentos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-amber-400 transition-all"
          onClick={() => goToClientes('vencendo_7dias')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Vencendo em 7 dias</p>
                <p className="text-3xl font-bold text-amber-600 font-['Barlow_Condensed']">
                  {stats?.vencendo_em_breve || 0}
                </p>
                <p className="text-xs text-slate-400">Planos Móveis</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center">
                <Clock className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-red-400 transition-all"
          onClick={() => goToClientes('vencidos')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Vencidos (Móvel)</p>
                <p className="text-3xl font-bold text-red-600 font-['Barlow_Condensed']">
                  {stats?.vencidos_movel || 0}
                </p>
                <p className="text-xs text-slate-400">Precisam recarga</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-violet-400 transition-all" 
          data-testid="clientes-combo-card"
          onClick={() => goToClientes('combo')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Clientes Combo</p>
                <p className="text-3xl font-bold text-violet-600 font-['Barlow_Condensed']">
                  {stats?.clientes_combo || 0}
                </p>
                <p className="text-xs text-slate-400">Fibra + Móvel</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-violet-500 flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bg-white border-slate-200 shadow-sm card-hover cursor-pointer hover:border-red-400 transition-all" 
          data-testid="clientes-atraso-card"
          onClick={() => goToClientes('em_atraso')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm mb-1">Em Atraso</p>
                <p className="text-3xl font-bold text-red-600 font-['Barlow_Condensed']">
                  {stats?.clientes_em_atraso || 0}
                </p>
                <p className="text-xs text-slate-400">Status financeiro</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SEÇÃO DE ALERTAS - PLANOS MÓVEIS */}
      {(stats?.vencidos_movel > 0 || stats?.vencendo_hoje > 0 || stats?.vencendo_3dias > 0 || stats?.vencendo_7dias > 0) && (
        <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200 shadow-lg" data-testid="alertas-movel">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-700 font-['Barlow_Condensed'] flex items-center gap-2 text-xl">
              <AlertTriangle className="w-6 h-6 text-red-600 animate-pulse" />
              🚨 ALERTAS - PLANOS MÓVEIS PRÉ-PAGO
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Cards de resumo de alertas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div 
                className="bg-red-600 text-white p-4 rounded-xl cursor-pointer hover:bg-red-700 transition-all shadow-lg"
                onClick={function() { goToClientes('vencidos'); }}
              >
                <p className="text-red-200 text-sm font-medium">VENCIDOS</p>
                <p className="text-4xl font-bold font-['Barlow_Condensed']">{stats?.vencidos_movel || 0}</p>
                <p className="text-red-200 text-xs">Precisam recarga urgente</p>
              </div>
              
              <div 
                className="bg-red-500 text-white p-4 rounded-xl cursor-pointer hover:bg-red-600 transition-all shadow-lg"
                onClick={function() { goToClientes('vencendo_hoje'); }}
              >
                <p className="text-red-200 text-sm font-medium">VENCE HOJE</p>
                <p className="text-4xl font-bold font-['Barlow_Condensed']">{stats?.vencendo_hoje || 0}</p>
                <p className="text-red-200 text-xs">Ação imediata</p>
              </div>
              
              <div 
                className="bg-orange-500 text-white p-4 rounded-xl cursor-pointer hover:bg-orange-600 transition-all shadow-lg"
                onClick={function() { goToClientes('vencendo_3dias'); }}
              >
                <p className="text-orange-200 text-sm font-medium">PRÓX. 3 DIAS</p>
                <p className="text-4xl font-bold font-['Barlow_Condensed']">{stats?.vencendo_3dias || 0}</p>
                <p className="text-orange-200 text-xs">Entrar em contato</p>
              </div>
              
              <div 
                className="bg-amber-500 text-white p-4 rounded-xl cursor-pointer hover:bg-amber-600 transition-all shadow-lg"
                onClick={function() { goToClientes('vencendo_7dias'); }}
              >
                <p className="text-amber-200 text-sm font-medium">PRÓX. 7 DIAS</p>
                <p className="text-4xl font-bold font-['Barlow_Condensed']">{stats?.vencendo_7dias || 0}</p>
                <p className="text-amber-200 text-xs">Agendar contato</p>
              </div>
            </div>

            {/* Link para ver todos */}
            <div className="text-center">
              <button 
                onClick={function() { goToClientes('movel'); }}
                className="text-red-600 hover:text-red-800 font-medium underline"
              >
                Ver todos os {stats?.clientes_movel || 0} clientes de Telefonia Móvel →
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por Filial - Clientes */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="chart-filial">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-700" />
              CLIENTES POR FILIAL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.por_filial || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" fontSize={12} />
                  <YAxis 
                    dataKey="filial" 
                    type="category" 
                    stroke="#64748B" 
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#1E293B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tipo de Cliente - Pie */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="chart-tipo">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" />
              DISTRIBUIÇÃO POR TIPO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center justify-center">
              {tipoClienteData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tipoClienteData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {tipoClienteData.map(function(entry, index) {
                        return <Cell key={'cell-' + index} fill={COLORS[index % COLORS.length]} />;
                      })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400">Sem dados para exibir</p>
              )}
            </div>
            {tipoClienteData.length > 0 && (
              <div className="flex justify-center gap-6 mt-4">
                {tipoClienteData.map(function(item, index) {
                  return (
                    <div key={item.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-slate-500 text-sm">{item.name}: {item.value}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status Financeiro & Por Plano */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Financeiro */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="chart-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              STATUS FINANCEIRO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-600">Em Dia</span>
                </div>
                <span className="text-2xl font-bold text-emerald-600 font-['Barlow_Condensed']">
                  {stats?.clientes_em_dia || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-slate-600">Em Atraso</span>
                </div>
                <span className="text-2xl font-bold text-red-600 font-['Barlow_Condensed']">
                  {stats?.clientes_em_atraso || 0}
                </span>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-slate-500 mb-2">
                  <span>Taxa de Adimplência</span>
                  <span>
                    {stats?.total_clientes ? 
                      Math.round((stats.clientes_em_dia / stats.total_clientes) * 100) : 0}%
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${stats?.total_clientes ? 
                        (stats.clientes_em_dia / stats.total_clientes) * 100 : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Por Plano */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="chart-plano">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 font-['Barlow_Condensed'] flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" />
              CLIENTES POR PLANO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(stats?.por_plano || []).slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="plano" 
                    stroke="#64748B" 
                    fontSize={12}
                  />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#F97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
